import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { paths } from '@app/constants';
import { showDefaultErrorToast } from '@app/utils';
import { useDeleteQuestion as useDeleteQuestionAction, useQuestionDetails } from '@data/hooks';

enum BlockType {
  interacted = 'interacted',
  linked = 'linked',
}

export const useDeleteQuestion = () => {
  const navigate = useNavigate();

  const [isPopupOpen, setPopupOpen] = useState(false);

  const { state } = useLocation();
  const { questionId = '' } = useParams<{ questionId: string }>();

  const { mutate, isLoading } = useDeleteQuestionAction();
  const { data: question, isLoading: isQuestionLoading, refetch } = useQuestionDetails(questionId);

  const previousSearch = state?.from?.search || '';

  const disabled: BlockType | false = useMemo(() => {
    if (question?.skippedCount || question?.answeredCount) {
      return BlockType.interacted;
    }
    if (question?.trialCount) {
      return BlockType.linked;
    }
    return false;
  }, [question]);

  const openPopup = () => setPopupOpen(true);
  const closePopup = () => setPopupOpen(false);
  const handleButtonHover = () => refetch();

  const deleteQuestion = () => {
    mutate(questionId, {
      onSuccess: () => {
        navigate(`/${paths.app}/${paths.questions_library.root}${previousSearch || ''}`, {
          replace: true,
        });
      },
      onError: showDefaultErrorToast,
    });
  };

  const interactionLink: string = useMemo(() => {
    if (disabled === BlockType.interacted) {
      return `/${paths.app}/${paths.questions_library.root}/${questionId}/${paths.questions_library.merge}`;
    }
    if (disabled === BlockType.linked) {
      return `/${paths.app}/${paths.questions_library.root}/${questionId}/${paths.questions_library.trials}`;
    }
    return '';
  }, [disabled]);

  return {
    disabled,
    isPopupOpen,
    interactionLink,
    isLoading: isLoading || isQuestionLoading,
    openPopup,
    closePopup,
    deleteQuestion,
    handleButtonHover,
  };
};
